import type { RegionDefinition, RegionId } from "../types/game";

export const regions: Record<RegionId, RegionDefinition> = {
  sunmeadow: {
    id: "sunmeadow",
    name: { en: "Sunmeadow Vale", bs: "Dolina Sunčanih Livada", de: "Sonnenwiesental" },
    subtitle: { en: "The First Beacon", bs: "Prvi Svjetionik", de: "Das erste Leuchtfeuer" },
    story: { en: "A golden valley where even the scarecrows hold militia rank.", bs: "Zlatna dolina u kojoj čak i strašila imaju vojni čin.", de: "Ein goldenes Tal, in dem selbst Vogelscheuchen Milizrang tragen." },
    colors: { ground: "#6f9d46", ground2: "#3e713e", path: "#d8b874", accent: "#ffd76b", shadow: "#243d2d" },
    weather: "breeze",
    mechanic: { en: "Windmills grant brief attack-speed gusts.", bs: "Vjetrenjače daju kratke nalete brzine napada.", de: "Windmühlen gewähren kurze Angriffsböen." },
  },
  elderwild: {
    id: "elderwild",
    name: { en: "Elderwild", bs: "Prastara Šuma", de: "Urwaldhain" },
    subtitle: { en: "Where roots remember", bs: "Gdje korijen pamti", de: "Wo Wurzeln erinnern" },
    story: { en: "The forest has begun returning borrowed footsteps—with interest.", bs: "Šuma je počela vraćati posuđene korake—s kamatom.", de: "Der Wald gibt geliehene Schritte zurück—mit Zinsen." },
    colors: { ground: "#287e6a", ground2: "#18534f", path: "#a9c083", accent: "#77f1ce", shadow: "#122f35" },
    weather: "fireflies",
    mechanic: { en: "Moonroots periodically heal creatures near ancient trees.", bs: "Mjesečevo korijenje povremeno liječi bića kraj drevnih stabala.", de: "Mondwurzeln heilen regelmäßig Wesen an alten Bäumen." },
  },
  suncleft: {
    id: "suncleft",
    name: { en: "Suncleft Expanse", bs: "Raspolućena Pustinja", de: "Sonnenspalt-Weite" },
    subtitle: { en: "The buried sky", bs: "Zakopano nebo", de: "Der begrabene Himmel" },
    story: { en: "An observatory fell here. The astronomers insist the sky fell first.", bs: "Ovdje je pao opservatorij. Astronomi tvrde da je nebo palo prvo.", de: "Hier fiel ein Observatorium. Die Astronomen behaupten, der Himmel fiel zuerst." },
    colors: { ground: "#bd843e", ground2: "#815127", path: "#edcf8b", accent: "#70d6c8", shadow: "#4d3027" },
    weather: "sand",
    mechanic: { en: "Sandstorms shorten tower range until the warning passes.", bs: "Pješčane oluje smanjuju domet tornjeva dok upozorenje ne prođe.", de: "Sandstürme verkürzen die Turmreichweite bis zur Entwarnung." },
  },
  gloamfen: {
    id: "gloamfen",
    name: { en: "Gloamfen", bs: "Sumračna Močvara", de: "Dämmermoor" },
    subtitle: { en: "Lanterns lie here", bs: "Fenjeri ovdje lažu", de: "Laternen lügen hier" },
    story: { en: "Follow the blue lights, said nobody who ever came home.", bs: "Prati plava svjetla, reče niko ko se ikad vratio.", de: "Folge den blauen Lichtern, sagte niemand, der je heimkam." },
    colors: { ground: "#57466b", ground2: "#332d4d", path: "#9c886d", accent: "#b778dd", shadow: "#201d35" },
    weather: "mist",
    mechanic: { en: "Mist conceals stealth enemies until they draw close.", bs: "Magla skriva nevidljive neprijatelje dok se ne približe.", de: "Nebel verbirgt Schleichfeinde, bis sie nahe sind." },
  },
  frostcrown: {
    id: "frostcrown",
    name: { en: "Frostcrown Range", bs: "Planine Ledene Krune", de: "Frostkronengebirge" },
    subtitle: { en: "The bell under ice", bs: "Zvono pod ledom", de: "Die Glocke unter Eis" },
    story: { en: "The peaks ring at midnight. Nobody installed bells.", bs: "Vrhovi zvone u ponoć. Niko nije postavio zvona.", de: "Die Gipfel läuten um Mitternacht. Niemand hat Glocken gebaut." },
    colors: { ground: "#8ec4d5", ground2: "#527fa4", path: "#d6e7dc", accent: "#c6f4ff", shadow: "#263e62" },
    weather: "snow",
    mechanic: { en: "Deep cold briefly freezes random build sites.", bs: "Duboka hladnoća nakratko zamrzava nasumična gradilišta.", de: "Tiefe Kälte friert kurz zufällige Bauplätze ein." },
  },
  cinderfast: {
    id: "cinderfast",
    name: { en: "Cinderfast", bs: "Tvrđava Žara", de: "Glutfeste" },
    subtitle: { en: "Forge of the false crown", bs: "Kovačnica lažne krune", de: "Schmiede der falschen Krone" },
    story: { en: "A fortress fueled by stolen beacon-fire and truly dreadful ventilation.", bs: "Tvrđava koju hrani ukradena vatra svjetionika i užasna ventilacija.", de: "Eine Festung aus gestohlenem Leuchtfeuer und wirklich furchtbarer Lüftung." },
    colors: { ground: "#4b3430", ground2: "#2b252a", path: "#856d59", accent: "#ff7138", shadow: "#17131a" },
    weather: "embers",
    mechanic: { en: "Eruptions mark dangerous ground before striking.", bs: "Erupcije označe opasno tlo prije udara.", de: "Eruptionen markieren gefährlichen Boden vor dem Einschlag." },
  },
  starless: {
    id: "starless",
    name: { en: "The Starless Verge", bs: "Bezzvjezdani Rub", de: "Der Sternlose Rand" },
    subtitle: { en: "Beyond the final road", bs: "Iza posljednjeg puta", de: "Jenseits des letzten Weges" },
    story: { en: "The road ends. The enemy, irritatingly, does not.", bs: "Put završava. Neprijatelj, iritantno, ne.", de: "Der Weg endet. Der Feind, ärgerlicherweise, nicht." },
    colors: { ground: "#292354", ground2: "#14132f", path: "#665788", accent: "#b16eff", shadow: "#08091b" },
    weather: "rift",
    mechanic: { en: "Rifts may bend enemies forward or backward along the path.", bs: "Pukotine mogu saviti neprijatelje naprijed ili nazad duž staze.", de: "Risse versetzen Feinde vor oder zurück auf dem Pfad." },
  },
};

export const regionOrder: RegionId[] = ["sunmeadow", "elderwild", "suncleft", "gloamfen", "frostcrown", "cinderfast", "starless"];
